import { z } from 'zod';

import { BoardItemKindSchema, PartialReasonSchema } from './board';
import { GitHubCoverageSchema } from './github-coverage';
import { IssueProviderSchema } from './issues';

// ---------------------------------------------------------------------------
// Next up — a deterministic "what should I work on next?" projection over the
// board (issue #201). It reads the already-computed `kruxitRank` axes plus the
// board's relationship buckets and emits a short, ranked list of suggestions,
// each carrying machine-readable reason codes. No scoring is invented here and
// no LLM is involved: this is a *surfacing* layer over the existing ordering,
// so the dashboard panel, the HTTP route and the MCP tool can never disagree.
//
// Reason `text` is deliberately English and DX-facing (it ships to agents over
// MCP); the dashboard renders the `code` through i18n rather than the text. It
// is a *definition* of the code, identical on every row that carries it — the
// per-row instance (`PR #2946, 2h`) travels separately as `evidence`.
// ---------------------------------------------------------------------------

/**
 * What the user should actually *do* with the item. `finish` = in-flight work of
 * theirs to push over the line; `review` = someone is waiting on their review;
 * `respond` = their own PR came back with changes requested; `start` = nothing is
 * in flight, pick this up next; `batch` = do it alongside a sibling in the same area.
 */
export const NEXT_WORK_ACTIONS = [
  'verify',
  'finish',
  'review',
  'respond',
  'start',
  'batch',
  'pause',
  'wait',
] as const;
export const NextWorkActionSchema = z.enum(NEXT_WORK_ACTIONS);
export type NextWorkAction = z.infer<typeof NextWorkActionSchema>;

/**
 * Which heuristic surfaced the suggestion. The first three mirror the
 * `kruxit-*` ordering axes; `parallel` is this projection's own pass, which
 * compares changed-file lists and only claims what that evidence supports.
 */
export const NEXT_WORK_AXES = ['finish', 'urgent', 'area', 'parallel'] as const;
export const NextWorkAxisSchema = z.enum(NEXT_WORK_AXES);
export type NextWorkAxis = z.infer<typeof NextWorkAxisSchema>;

/**
 * Machine-readable justifications. Agents branch on these; the dashboard maps
 * them to i18n chip labels. A suggestion can carry none when every applicable
 * code is ubiquitous in the returned result.
 */
export const NEXT_WORK_REASONS = [
  /** Ranks top of the `kruxit-finish` axis — in-flight and closest to done. */
  'closest-to-done',
  /** Approved and open: merging it unblocks whatever is stacked behind it. */
  'ready-to-merge',
  /** A reviewer asked for changes — the caller's own PR is parked until they reply. */
  'changes-requested',
  /**
   * A recent CI check failed on the caller's own PR, so it is not mergeable
   * until fixed. Emitted alongside the suppression of `ready-to-merge`, so the
   * loss of that positive signal is explained rather than silent.
   */
  'checks-failing',
  /** Someone requested the user's review; their turn blocks the author. */
  'blocks-others',
  /** Completion directly unblocks at least two open GitHub issues. */
  'unblocks-n',
  /** Native GitHub dependencies say an open blocker remains. */
  'blocked-by',
  /** Its final blocker or direct child closed recently, making this newly actionable. */
  'just-unblocked',
  /** Explicit non-blocking issue-body relation used only for organisation. */
  'related',
  /** The issue body declares a step that must happen before implementation. */
  'has-prerequisite-step',
  /** The issue body explicitly identifies this as a parked planning record. */
  'parked',
  /**
   * The issue body structurally declares a living tracker — a ledger whose own
   * checklist is unblocked while the implementation it tracks waits elsewhere.
   * A tracker is never the actionable unit, so it never occupies a `start` slot.
   */
  'tracker',
  /** Another open issue in the fetched window explicitly owns this delivery. */
  'delivered-by',
  'landed',
  'in-flight',
  /** More actionable owned work is in progress than the configured WIP limit. */
  'wip-limit-exceeded',
  'via-parent',
  'blocked-by-children',
  /** Sits in the top severity band of the `kruxit-urgent` axis. */
  'urgent',
  /** Shares a `kruxit-area` cluster with another suggestion — batch them. */
  'same-area',
  /**
   * Changed-file lists were compared against every higher-ranked suggestion and
   * none overlap. Deliberately NOT called "parallel-safe": it is emitted only
   * when real file evidence exists on both sides, and file overlap is a lower
   * bound on conflict, so it means "no conflict we could detect" — never
   * "provably independent".
   */
  'no-known-file-conflict',
  /** Same scope as work the user merged recently — cheap context re-entry. */
  'continues-recent-work',
  /**
   * Nothing more pressing applies — it is simply next in the Kruxit order. This
   * fallback participates in ranking before ubiquitous display reasons are
   * suppressed, so it need not appear in the final reasons array.
   */
  'next-in-order',
] as const;
export const NextWorkReasonCodeSchema = z.enum(NEXT_WORK_REASONS);
export type NextWorkReasonCode = z.infer<typeof NextWorkReasonCodeSchema>;

/** How much the heuristics trust the suggestion; drives chip prominence only. */
export const NEXT_WORK_CONFIDENCE = ['high', 'medium', 'low'] as const;
export const NextWorkConfidenceSchema = z.enum(NEXT_WORK_CONFIDENCE);
export type NextWorkConfidence = z.infer<typeof NextWorkConfidenceSchema>;

/**
 * How long a row's reason evidence may be. Bounded so a hostile issue body can
 * never inflate a row: the longest source (`prerequisiteStep` / `parked` /
 * `tracker`) is itself capped at 160, and the projection clamps to this.
 */
export const NEXT_WORK_EVIDENCE_MAX = 160;

/**
 * One justification: a code to branch on, the DX-facing English *definition* of
 * that code, and — when the projection found one — the concrete instance behind
 * it on THIS row.
 *
 * The split matters (issue #2960). `text` is the same sentence for every row
 * carrying the code, so a renderer can emit it once in a legend; `evidence` is
 * the reference id and age that differ per row (`PR #2946, 2h`). Folding the
 * instance into `text` made a legend collapse two rows' evidence into whichever
 * was written last, so a fresh merge and a fortnight-old one read identically.
 */
export const NextWorkReasonSchema = z.object({
  code: NextWorkReasonCodeSchema,
  /** Short English sentence fragment. Never rendered by the dashboard. */
  text: z.string().min(1),
  /**
   * The bounded, deterministic instance behind the code on this row — a
   * reference id plus an age where one exists. Absent when the code is a
   * standing property of the item with nothing to point at.
   */
  evidence: z.string().min(1).max(NEXT_WORK_EVIDENCE_MAX).optional(),
});
export type NextWorkReason = z.infer<typeof NextWorkReasonSchema>;

/**
 * The item a suggestion points at — the identifying subset of `BoardItem`, so a
 * consumer can render or open it without re-fetching the board.
 */
export const NextWorkItemReferenceSchema = z.object({
  /** `BoardItem.id` — the stable handle `conflictsWith` entries refer to. */
  id: z.string().min(1),
  provider: IssueProviderSchema,
  kind: BoardItemKindSchema,
  /** Repo (`owner/name`) or Linear `workspace/team`. */
  scope: z.string(),
  /** Short human key: `#123` (GitHub) or `ENG-45` (Linear). */
  key: z.string(),
  title: z.string(),
  url: z.string(),
  /** Explicit native-dependency marker; always emitted by the live projection. */
  blocked: z.boolean().optional(),
  /** Exact count behind `blocked`; may exceed the bounded `blockedBy` keys. */
  openBlockers: z.number().int().nonnegative().optional(),
  /** Bounded human keys for the open blockers (for example `#123`). */
  blockedBy: z.array(z.string()).optional(),
  /** Exact number of open issues this item completes would unblock. */
  unblocks: z.number().int().nonnegative().optional(),
  /** Parent issue key, when this is a native GitHub sub-issue. */
  parent: z.string().nullable().optional(),
  /** Exact unfinished direct-child count. */
  openSubIssues: z.number().int().nonnegative().optional(),
});
export type NextWorkItemReference = z.infer<typeof NextWorkItemReferenceSchema>;

const EvidenceParametersSchema = z.record(z.string(), z.string()).optional();
const ConfidenceEvidenceSchema = z.object({
  code: z.enum([
    'primary-signal',
    'priority-source',
    'issue-body-relationship',
    'missing-path-scope',
    'missing-first-class-dependency',
    'leading-signal-insufficient',
    'add-dependency-relationship',
    'add-path-scope-acceptance',
  ]),
  parameters: EvidenceParametersSchema,
});
const LaneEvidenceSchema = z.object({
  code: z.enum([
    /**
     * A provider recorded that this item overlaps `parameters.target` — a Linear
     * duplicate/related relation, or two items claiming the same `Closes #N`.
     * Ranked above `shared-changed-paths` because it is someone's statement
     * rather than kruxit's inference. See `work-relationships.ts` (#2877).
     */
    'asserted-overlap',
    'shared-changed-paths',
    'shared-area',
    'open-dependency',
    'declared-coordination',
    'no-known-path-overlap',
    'insufficient-collision-evidence',
  ]),
  parameters: EvidenceParametersSchema,
});

/**
 * Every field that must travel with its reason code, and vice versa. Both
 * directions matter: a `deliveredBy` with no `delivered-by` reason renders a
 * chip the legend cannot explain, and the reason with no field names no owner.
 */
const PAIRED_ANNOTATIONS = [
  ['deliveredBy', 'delivered-by'],
  ['related', 'related'],
  ['prerequisiteStep', 'has-prerequisite-step'],
  ['parked', 'parked'],
  ['tracker', 'tracker'],
] as const;

type PairedField = (typeof PAIRED_ANNOTATIONS)[number][0];

interface PairedSuggestion {
  readonly reasons: readonly NextWorkReason[];
  readonly deliveredBy?: string | undefined;
  readonly related?: string | undefined;
  readonly prerequisiteStep?: string | undefined;
  readonly parked?: string | undefined;
  readonly tracker?: string | undefined;
}

function requireReasonForAnnotation(
  suggestion: PairedSuggestion,
  context: z.RefinementCtx,
  field: PairedField,
  code: NextWorkReasonCode,
): void {
  const hasReason = suggestion.reasons.some((reason) => reason.code === code);
  if (suggestion[field] === undefined && hasReason) {
    context.addIssue({
      code: 'custom',
      message: `${field} is required for the ${code} reason`,
      path: [field],
    });
  }
  if (suggestion[field] !== undefined && !hasReason) {
    context.addIssue({
      code: 'custom',
      message: `${code} reason is required when ${field} is present`,
      path: ['reasons'],
    });
  }
}

/**
 * A decayed code carries no ranking weight, so displaying it as a reason is
 * exactly the presentation this field exists to prevent.
 */
function requireDecayedNotDisplayed(
  suggestion: {
    readonly reasons: readonly NextWorkReason[];
    readonly decayedReasons?: readonly NextWorkReason[] | undefined;
  },
  context: z.RefinementCtx,
): void {
  const displayed = new Set(suggestion.reasons.map((reason) => reason.code));
  const alsoDisplayed = (suggestion.decayedReasons ?? []).filter((reason) =>
    displayed.has(reason.code),
  );
  if (alsoDisplayed.length === 0) return;
  context.addIssue({
    code: 'custom',
    message: `a decayed reason must not also be displayed: ${alsoDisplayed
      .map((reason) => reason.code)
      .join(', ')}`,
    path: ['decayedReasons'],
  });
}

/**
 * A `verify` row exists ONLY because a merged pull request was found, so it must
 * say which one. "Verify" with nothing named is an instruction the reader cannot
 * follow, which is exactly what issue #2960 observed live on #2851.
 *
 * This pairs the `verify` action with the `landed` reason the way
 * `PAIRED_ANNOTATIONS` pairs a field with its code — so `landed` is exempt from
 * ubiquity suppression in the projection for the same reason those codes are.
 */
function requireVerifyNamesLandedEvidence(
  suggestion: {
    readonly action: NextWorkAction;
    readonly reasons: readonly NextWorkReason[];
  },
  context: z.RefinementCtx,
): void {
  if (suggestion.action !== 'verify') return;
  const landed = suggestion.reasons.find((reason) => reason.code === 'landed');
  if (landed?.evidence !== undefined) return;
  context.addIssue({
    code: 'custom',
    message:
      'a verify suggestion must carry a landed reason naming its merged pull request',
    path: ['reasons'],
  });
}

/** One recommendation. */
export const NextWorkSuggestionSchema = z
  .object({
    item: NextWorkItemReferenceSchema,
    action: NextWorkActionSchema,
    /** Discriminating displayed reasons; ubiquitous codes are omitted. */
    reasons: z.array(NextWorkReasonSchema),
    confidence: NextWorkConfidenceSchema,
    /** The evidence behind confidence, including concrete ways to improve it. */
    confidenceEvidence: z
      .object({
        available: z.array(ConfidenceEvidenceSchema),
        missing: z.array(ConfidenceEvidenceSchema),
        reducedBecause: ConfidenceEvidenceSchema.nullable(),
        remedies: z.array(ConfidenceEvidenceSchema),
      })
      .optional(),
    /** Every deterministic ordering key, in application order. */
    ordering: z
      .object({
        keys: z.array(z.object({ key: z.string(), value: z.string() })),
        finalTieBreak: z.string(),
        prioritySource: z.enum(['explicit', 'derived', 'none']),
      })
      .optional(),
    /** 1-based position in the returned list (smaller = do sooner). */
    rank: z.number().int().positive(),
    axis: NextWorkAxisSchema,
    /**
     * Set when the suggestion belongs to a batch of same-area work: the
     * `KruxitAreaGroup.key` its siblings share. Absent when it stands alone.
     */
    parallelGroup: z.string().optional(),
    /**
     * `BoardItem.id`s of higher-ranked suggestions this one overlaps with, so a
     * consumer can avoid picking two items that do the same work. Populated from
     * shared changed-file paths plus provider-asserted overlap edges (#2877) —
     * `laneEvidence` says which applied. Absence is *not* a guarantee of
     * independence: issues carry no file list at all, providers assert only what
     * someone bothered to record, and two PRs can clash without sharing a path.
     */
    conflictsWith: z.array(z.string()).optional(),
    /** Bounded parallel-planning conclusion; missing evidence is always unknown. */
    lane: z.enum(['parallel', 'sequence', 'unknown']).optional(),
    laneEvidence: z.array(LaneEvidenceSchema).optional(),
    /** Display key of the open issue that claims this item's delivery. */
    deliveredBy: z.string().min(1).optional(),
    /** Deterministic first same-repo soft relation, when one was declared. */
    related: z.string().min(1).optional(),
    /** Capped first prerequisite step declared by the issue body. */
    prerequisiteStep: z.string().min(1).max(160).optional(),
    /** Capped issue-body explanation for a listed-but-demoted planning record. */
    parked: z.string().min(1).max(160).optional(),
    /** Capped issue-body explanation for a listed-but-never-startable tracker. */
    tracker: z.string().min(1).max(160).optional(),
    /**
     * Reasons whose deterministic decay has reached zero weight. Informational
     * only: they are removed from `reasons`, so a code that no longer carries
     * ranking weight can never headline a row — but the evidence itself is
     * still reported rather than silently dropped.
     */
    decayedReasons: z.array(NextWorkReasonSchema).optional(),
  })
  .superRefine((suggestion, context) => {
    for (const [field, code] of PAIRED_ANNOTATIONS) {
      requireReasonForAnnotation(suggestion, context, field, code);
    }
    requireDecayedNotDisplayed(suggestion, context);
    requireVerifyNamesLandedEvidence(suggestion, context);
  });
export type NextWorkSuggestion = z.infer<typeof NextWorkSuggestionSchema>;

/** Why a fetched item was withheld from ranking before any scoring ran. */
export const NEXT_WORK_EXCLUSIONS = ['bot-authored', 'pinned'] as const;
export const NextWorkExclusionReasonSchema = z.enum(NEXT_WORK_EXCLUSIONS);
export type NextWorkExclusionReason = z.infer<
  typeof NextWorkExclusionReasonSchema
>;

/**
 * One item the projection refused to rank. Automation-maintained records are
 * dropped before scoring rather than demoted, so reporting them here is what
 * keeps the omission debuggable instead of silent.
 */
export const NextWorkExclusionSchema = z.object({
  /** `BoardItem.id`, so the row can be joined back to the board. */
  id: z.string().min(1),
  /** Short human key: `#123` (GitHub) or `ENG-45` (Linear). */
  key: z.string(),
  /** Repo (`owner/name`) or Linear `workspace/team`. */
  scope: z.string(),
  reason: NextWorkExclusionReasonSchema,
});
export type NextWorkExclusion = z.infer<typeof NextWorkExclusionSchema>;

/**
 * How many exclusions are reported. The list is diagnostic, not a second
 * result set, so a board full of bot noise cannot inflate the payload.
 */
export const NEXT_WORK_EXCLUSION_LIMIT = 20;

/** Response envelope for `GET /api/account/next-work`. */
export const NextWorkResponseSchema = z.object({
  githubCoverage: GitHubCoverageSchema.optional(),
  /** True when at least one provider is connected. */
  connected: z.boolean(),
  /** True when a provider/bucket failed — suggestions may be missing work. */
  partial: z.boolean(),
  /** Why it's partial (`auth` ⇒ reconnect the PAT); `null` when not partial. */
  partialReason: PartialReasonSchema.nullable().default(null),
  /** Ranked, capped at `NEXT_WORK_LIMIT`. Empty when nothing is actionable. */
  suggestions: z.array(NextWorkSuggestionSchema),
  /** Items withheld from ranking, capped at `NEXT_WORK_EXCLUSION_LIMIT`. */
  excluded: z.array(NextWorkExclusionSchema).default([]),
});
export type NextWorkResponse = z.infer<typeof NextWorkResponseSchema>;

/**
 * How many suggestions the projection returns at most. Ten is the ceiling the
 * MCP surface ships; the dashboard panel renders the leading five.
 */
export const NEXT_WORK_LIMIT = 10;

/** How many suggestions the dashboard's "Next up" panel shows. */
export const NEXT_WORK_PANEL_LIMIT = 5;

/**
 * Hard cap on a board people-filter, shared by `/api/account/work-items` and
 * `/api/account/next-work`. The list fans out provider queries and feeds
 * `boardScopeKey` (hence the distinct snapshot rows a user can create), so an
 * unbounded one is a cost lever rather than a useful filter. Kept here
 * because the two routes are meant to agree on scope — see #2600.
 */
export const MAX_BOARD_PEOPLE = 50;
